/**
 * src/components/support/TicketCard.js
 *
 * Compact ticket summary card  links to ticket detail page.
 * Green/neutral palette consistent with Orrith - Cycle of Certainty UI.
 */

import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, MessageCircle } from "lucide-react";
import { errorDev } from '../../utils/logger.js';

const STATUS_STYLES = {
  open: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  resolved: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  closed: "bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400",
};

const STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

function fmtDate(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function TicketCard({ ticket }) {
  const { t, ready: i18nReady } = useTranslation("support");
  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  const status = ticket.status || "open";
  const replies = ticket.replies || [];

  return (
    <Link
      to={`/support/tickets/${ticket.id}`}
      className="group flex items-start gap-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 hover:border-green-500 dark:hover:border-green-600 transition"
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
        <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[240px]">
            {ticket.subject || safeT("ticket_no_subject", "No subject")}
          </h3>
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>
            {safeT(`status_${status}`, STATUS_LABELS[status] || status)}
          </span>
        </div>

        {ticket.lastMessageSnippet && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
            {ticket.lastMessageSnippet}
          </p>
        )}

        <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400 dark:text-gray-500">
          <span>{fmtDate(ticket.updatedAt || ticket.createdAt)}</span>
          {replies.length > 0 && (
            <span>
              {replies.length} {replies.length === 1
                ? safeT("ticket_reply", "reply")
                : safeT("ticket_replies", "replies")}
            </span>
          )}
          {ticket.category && (
            <span className="capitalize">{ticket.category}</span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-green-500 transition flex-shrink-0 mt-1" />
    </Link>
  );
}



